/**
 * WorkshopDetailHeader — breadcrumb + big title (venue · date · type), the
 * lifecycle pill, and the draft actions (BON-117 operator rework).
 *
 *  - Draft, not editing: [Discard] [Publish]
 *  - Otherwise:          no buttons. Edit lives on the details card.
 *
 * Discard uses an inline confirm swap (no modal). Breadcrumb is
 * `Venue > [venue] > Workshops > [date & time]`, the final segment formatted
 * "Fri, Oct 1st @ 6:30pm" (operator ruling), consistent regardless of entry.
 */
import { Link } from 'react-router-dom';

import { LifecyclePill } from './LifecyclePill';
import { SeriesPill } from './SeriesPill';
import type { WorkshopDetailHeaderProps } from './workshopDetailHeader.types';
import { formatBreadcrumbDateTime } from './workshopFields';
import { WorkshopHeaderActions } from './WorkshopHeaderActions';

const crumbSep = (
  <span style={{ color: 'var(--nb-faint)' }} aria-hidden="true">
    ›
  </span>
);

const crumbLink = { color: 'var(--nb-muted)', textDecoration: 'none' } as const;

export function WorkshopDetailHeader(props: WorkshopDetailHeaderProps) {
  const { workshop, venueHref, workshopsHref } = props;
  const typeLabel = workshop.workshopType.displayName ?? workshop.workshopType.name;
  const dateTime = formatBreadcrumbDateTime(workshop.localStartDatetime);
  const title = `${workshop.venueName} · ${dateTime} · ${typeLabel}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <nav
        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}
        aria-label="Breadcrumb"
      >
        <span style={{ color: 'var(--nb-subtle)' }}>Venue</span>
        {crumbSep}
        {venueHref ? (
          <Link to={venueHref} style={crumbLink}>
            {workshop.venueName}
          </Link>
        ) : (
          <span style={{ color: 'var(--nb-muted)' }}>{workshop.venueName}</span>
        )}
        {crumbSep}
        <Link to={workshopsHref} style={crumbLink}>
          Workshops
        </Link>
        {crumbSep}
        <span style={{ color: 'var(--nb-subtle)' }}>{dateTime}</span>
      </nav>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <h2
          className="nb-display"
          style={{
            margin: 0,
            fontSize: 22,
            color: 'var(--nb-bark-dark)',
            letterSpacing: '.01em',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </h2>
        <LifecyclePill lifecycle={workshop.lifecycle} startsAt={workshop.startsAt} />
        {workshop.series && <SeriesPill series={workshop.series} />}
        <span style={{ flex: 1 }} />
        <WorkshopHeaderActions {...props} />
      </div>
    </div>
  );
}
